import { Button, Select, Spin } from "antd";
import L from "leaflet";
import { useEffect, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import { StatusTag } from "@/shared/components/StatusTag";
import {
	createMovementCarIcon,
	MOVEMENT_STATE_COLORS,
	MOVEMENT_STATE_LABELS,
} from "@/shared/map/icons";
import { useEquipmentLive } from "../hooks/useEquipment";
import type { LiveEquipmentPosition } from "../types";

const STATUS_OPTIONS = [
	{ value: "online", label: "Online" },
	{ value: "offline", label: "Offline" },
	{ value: "error", label: "Xato" },
];

const DEFAULT_CENTER: [number, number] = [41.56, 60.62];
const DEFAULT_ZOOM = 7;

function FitAllBounds({ positions }: { positions: [number, number][] }) {
	const map = useMap();
	useEffect(() => {
		if (positions.length === 0) {
			return;
		}
		if (positions.length === 1) {
			map.setView(positions[0], 14);
			return;
		}
		const bounds = L.latLngBounds(positions);
		map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
	}, [map, positions]);
	return null;
}

function EquipmentPopup({
	eq,
	onViewGps,
}: {
	eq: LiveEquipmentPosition;
	onViewGps: (id: string) => void;
}) {
	if (!eq.position) {
		return null;
	}
	const pos = eq.position;
	return (
		<div className="text-sm min-w-[180px] space-y-0.5">
			<p className="font-semibold text-base leading-snug">{eq.equipmentName}</p>
			{eq.type && <p className="text-gray-500 text-xs">{eq.type.name}</p>}
			{eq.licensePlate && (
				<p>
					Davlat raqami: <strong>{eq.licensePlate}</strong>
				</p>
			)}
			<p className="flex items-center gap-1">
				Holat:{" "}
				<span
					style={{ color: MOVEMENT_STATE_COLORS[eq.movementState] ?? "#6b7280" }}
					className="font-medium"
				>
					{MOVEMENT_STATE_LABELS[eq.movementState] ?? eq.movementState}
				</span>
			</p>
			{pos.speed !== null && (
				<p>
					Tezlik: <strong>{pos.speed} km/h</strong>
				</p>
			)}
			{pos.heading !== null && (
				<p>
					Yo'nalish: <strong>{pos.heading}°</strong>
				</p>
			)}
			{eq.assignedStaff && (
				<p>
					Haydovchi: <strong>{eq.assignedStaff.fullname}</strong>
				</p>
			)}
			<p className="text-gray-400 text-xs pt-0.5">{new Date(pos.receivedAt).toLocaleString()}</p>
			<Button
				type="primary"
				size="small"
				block
				style={{ marginTop: 8, cursor: "pointer" }}
				onClick={() => onViewGps(eq.equipmentId)}
			>
				GPS tarixini ko'rish
			</Button>
		</div>
	);
}

interface EquipmentLiveMapProps {
	organizationId?: string;
}

export function EquipmentLiveMap({ organizationId }: EquipmentLiveMapProps = {}) {
	const navigate = useNavigate();
	const [statusFilter, setStatusFilter] = useState<"online" | "offline" | "error" | undefined>();

	const { data, isLoading, isFetching, dataUpdatedAt } = useEquipmentLive({
		organizationId,
		status: statusFilter,
	});

	const items = data ?? [];
	const withPosition = items.filter(
		(
			e
		): e is LiveEquipmentPosition & { position: NonNullable<LiveEquipmentPosition["position"]> } =>
			e.position !== null
	);

	const positions: [number, number][] = withPosition.map((e) => [
		e.position.latitude,
		e.position.longitude,
	]);

	const movingCount = items.filter((e) => e.movementState === "MOVING").length;
	const stoppedCount = items.filter((e) => e.movementState === "STOPPED").length;
	const noSignalCount = items.filter((e) => e.movementState === "NO_SIGNAL").length;
	const neverConnectedCount = items.filter((e) => e.movementState === "NEVER_CONNECTED").length;

	return (
		<div className="space-y-3">
			{/* Stats + filters bar */}
			<div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50 border border-neutral-200 rounded-xl px-4 py-2.5">
				<div className="flex items-center gap-3 text-sm">
					<StatusTag tone="success">
						{MOVEMENT_STATE_LABELS.MOVING}: {movingCount}
					</StatusTag>
					<StatusTag tone="warning">
						{MOVEMENT_STATE_LABELS.STOPPED}: {stoppedCount}
					</StatusTag>
					<StatusTag tone="error">
						{MOVEMENT_STATE_LABELS.NO_SIGNAL}: {noSignalCount}
					</StatusTag>
					<StatusTag tone="default">
						{MOVEMENT_STATE_LABELS.NEVER_CONNECTED}: {neverConnectedCount}
					</StatusTag>
					<span className="text-ink-soft text-xs">Jami: {items.length}</span>
				</div>
				<div className="flex items-center gap-2">
					{(isLoading || isFetching) && <Spin size="small" />}
					{dataUpdatedAt > 0 && (
						<span className="text-xs text-ink-soft">
							{new Date(dataUpdatedAt).toLocaleTimeString()} da yangilandi
						</span>
					)}
					<Select
						placeholder="Holat"
						allowClear
						style={{ width: 130 }}
						value={statusFilter}
						onChange={(v) => setStatusFilter(v as typeof statusFilter)}
						options={STATUS_OPTIONS}
					/>
				</div>
			</div>

			{/* Legend */}
			<div className="flex items-center gap-4 text-xs text-ink-soft px-1">
				{Object.entries(MOVEMENT_STATE_COLORS).map(([s, c]) => (
					<span key={s} className="flex items-center gap-1.5">
						<svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill={c}>
							<path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99z" />
						</svg>
						{MOVEMENT_STATE_LABELS[s]}
					</span>
				))}
				<span className="ml-auto text-ink-soft">30 soniyada bir yangilanadi</span>
			</div>

			{/* Map */}
			<div className="h-[calc(100vh-220px)] w-full rounded-xl overflow-hidden border border-neutral-200 shadow-card">
				<MapContainer
					center={DEFAULT_CENTER}
					zoom={DEFAULT_ZOOM}
					className="h-full w-full"
					scrollWheelZoom
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					{positions.length > 0 && <FitAllBounds positions={positions} />}

					{withPosition.map((eq) => (
						<Marker
							key={eq.equipmentId}
							position={[eq.position.latitude, eq.position.longitude]}
							icon={createMovementCarIcon(eq.movementState, eq.position.heading)}
						>
							<Popup>
								<EquipmentPopup eq={eq} onViewGps={(id) => navigate(`/equipment/${id}`)} />
							</Popup>
						</Marker>
					))}
				</MapContainer>
			</div>
		</div>
	);
}
